import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/login', credentials);
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/logout');
    return response.data;
  },
  getUser: async () => {
    const response = await api.get('/user');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/profile', data);
    return response.data;
  },
  changePassword: async (data) => {
    const response = await api.post('/change-password', data);
    return response.data;
  },

  // Password reset methods
  forgotPassword: async (data) => {
    const response = await api.post('/forgot-password', data);
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await api.post('/reset-password', data);
    return response.data;
  },

  validateResetToken: async (data) => {
    const response = await api.post('/validate-reset-token', data);
    return response.data;
  },

  // OTP methods
  verifyOtp: async (data) => {
    const response = await api.post('/verify-otp', data);
    return response.data;
  },

  resendOtp: async (data) => {
    const response = await api.post('/resend-otp', data);
    return response.data;
  },
};

// Users API (Admin)
export const usersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },
  getUser: async (id) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },
  updateUser: async (id, userData) => {
    const response = await api.put(`/admin/users/${id}`, userData);
    return response.data;
  },
  updateZelleQR: async (id, qrData) => {
    const response = await api.put(`/admin/users/${id}/zelle-qr`, { zelle_qr_data: qrData });
    return response.data;
  },
  toggleUserStatus: async (id, suspensionData = null) => {
    const response = await api.post(`/admin/users/${id}/toggle-status`, suspensionData);
    return response.data;
  },
  resetPassword: async (id, data) => {
    // Allow admin to set a new password directly (no old password required)
    const response = await api.post(`/admin/users/${id}/reset-password`, data);
    return response.data;
  },
  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },
  generateRandomUser: async () => {
    const response = await api.post('/admin/users/generate-random');
    return response.data;
  },
  generateTransactionsForUser: async (id, transactionCount = 10) => {
    const response = await api.post(`/admin/users/${id}/generate-transactions`, {
      transaction_count: transactionCount
    });
    return response.data;
  },
  toggleOtpStatus: async (id, otpEnabled, reason = null) => {
    const response = await api.post(`/admin/users/${id}/toggle-otp`, {
      otp_enabled: otpEnabled,
      reason: reason
    });
    return response.data;
  },
};

// Accounts API (Admin)
export const accountsAPI = {
  getAccounts: async (params = {}) => {
    const response = await api.get('/admin/accounts', { params });
    return response.data;
  },
  getAccount: async (id) => {
    const response = await api.get(`/admin/accounts/${id}`);
    return response.data;
  },
  createAccount: async (accountData) => {
    const response = await api.post('/admin/accounts', accountData);
    return response.data;
  },
  updateAccount: async (id, accountData) => {
    const response = await api.put(`/admin/accounts/${id}`, accountData);
    return response.data;
  },
  freezeAccount: async (id) => {
    const response = await api.post(`/admin/accounts/${id}/freeze`);
    return response.data;
  },
  unfreezeAccount: async (id) => {
    const response = await api.post(`/admin/accounts/${id}/unfreeze`);
    return response.data;
  },
  adjustBalance: async (id, adjustmentData) => {
    const response = await api.post(`/admin/accounts/${id}/adjust-balance`, adjustmentData);
    return response.data;
  },
  getAccountTypes: async () => {
    const response = await api.get('/admin/accounts/types');
    return response.data;
  },
  getAccountStatuses: async () => {
    const response = await api.get('/admin/accounts/statuses');
    return response.data;
  },
  generateCompliantNumber: async (id) => {
    const response = await api.post(`/admin/accounts/${id}/generate-compliant-number`);
    return response.data;
  },
  fixAllNonCompliant: async () => {
    const response = await api.post('/admin/accounts/fix-all-non-compliant');
    return response.data;
  },
};

// Dashboard API (Admin)
export const dashboardAPI = {
  getDashboard: async () => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },
  getSystemStatus: async () => {
    const response = await api.get('/admin/system-status');
    return response.data;
  },
  getActivityLogs: async (params = {}) => {
    const response = await api.get('/admin/activity-logs', { params });
    return response.data;
  },
};

// Bill Payments API
export const billPaymentsAPI = {
  // Get all bill payments with filtering and pagination
  getBillPayments: async (params = {}) => {
    const response = await api.get('/admin/bill-payments', { params });
    return response.data;
  },
  
  // Create a new bill payment
  createBillPayment: async (data) => {
    const response = await api.post('/admin/bill-payments', data);
    return response.data;
  },
  
  // Get detailed payment information
  getBillPayment: async (id) => {
    const response = await api.get(`/admin/bill-payments/${id}`);
    return response.data;
  },
  
  // Update payment details (allows editing all fields regardless of status)
  updateBillPayment: async (id, data) => {
    const response = await api.put(`/admin/bill-payments/${id}`, data);
    return response.data;
  },
  
  // Delete a bill payment
  deleteBillPayment: async (id) => {
    const response = await api.delete(`/admin/bill-payments/${id}`);
    return response.data;
  },
  
  // Cancel a payment
  cancelBillPayment: async (id) => {
    const response = await api.post(`/admin/bill-payments/${id}/cancel`);
    return response.data;
  },
  
  // Process a pending payment
  processBillPayment: async (id) => {
    const response = await api.post(`/admin/bill-payments/${id}/process`);
    return response.data;
  },
  
  // Get bill payment statistics
  getBillPaymentStatistics: async () => {
    const response = await api.get('/admin/bill-payments/statistics');
    return response.data;
  },
  
  // Get all users for dropdown
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/bill-payments/users', { params });
    return response.data;
  },
  
  // Get all bank accounts for dropdown
  getAccounts: async (params = {}) => {
    const response = await api.get('/admin/bill-payments/accounts', { params });
    return response.data;
  },
  
  // Get all payees with pagination
  getPayees: async (params = {}) => {
    const response = await api.get('/admin/bill-payments/payees', { params });
    return response.data;
  },
  
  // Create a new payee
  createPayee: async (data) => {
    const response = await api.post('/admin/bill-payments/payees', data);
    return response.data;
  },
  
  // Get detailed payee information
  getPayee: async (id) => {
    const response = await api.get(`/admin/bill-payments/payees/${id}`);
    return response.data;
  },
  
  // Update a payee
  updatePayee: async (id, data) => {
    const response = await api.put(`/admin/bill-payments/payees/${id}`, data);
    return response.data;
  },
  
  // Delete a payee
  deletePayee: async (id) => {
    const response = await api.delete(`/admin/bill-payments/payees/${id}`);
    return response.data;
  },
  
  // Get payment categories
  getCategories: async () => {
    const response = await api.get('/admin/bill-payments/categories');
    return response.data;
  },
  
  // Get payment statuses
  getStatuses: async () => {
    const response = await api.get('/admin/bill-payments/statuses');
    return response.data;
  },
};

// Zelle API
export const zelleAPI = {
  // Get all Zelle transactions with filtering and pagination
  getTransactions: async (params = {}) => {
    const response = await api.get('/admin/zelle', { params });
    return response.data;
  },
  
  // Create a new Zelle transaction
  createTransaction: async (data) => {
    const response = await api.post('/admin/zelle', data);
    return response.data;
  },
  
  // Get detailed Zelle transaction information
  getTransaction: async (id) => {
    const response = await api.get(`/admin/zelle/${id}`);
    return response.data;
  },
  
  // Update Zelle transaction details
  updateTransaction: async (id, data) => {
    const response = await api.put(`/admin/zelle/${id}`, data);
    return response.data;
  },
  
  // Delete a Zelle transaction
  deleteTransaction: async (id) => {
    const response = await api.delete(`/admin/zelle/${id}`);
    return response.data;
  },
  
  // Cancel a Zelle transaction
  cancelTransaction: async (id) => {
    const response = await api.post(`/admin/zelle/${id}/cancel`);
    return response.data;
  },
  
  // Get Zelle transaction statistics
  getStatistics: async () => {
    const response = await api.get('/admin/zelle/statistics');
    return response.data;
  },
  
  // Get available Zelle transaction statuses
  getStatuses: async () => {
    const response = await api.get('/admin/zelle/statuses');
    return response.data;
  },
  
  // Get available Zelle transaction types
  getTypes: async () => {
    const response = await api.get('/admin/zelle/types');
    return response.data;
  },
  
  // Get all users for dropdown
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/zelle/users', { params });
    return response.data;
  },
  
  // Get all bank accounts for dropdown
  getAccounts: async (params = {}) => {
    const response = await api.get('/admin/zelle/accounts', { params });
    return response.data;
  },
};

// Crypto API
export const cryptoAPI = {
  // Get all crypto transactions with filtering and pagination
  getTransactions: async (params = {}) => {
    const response = await api.get('/admin/crypto/transactions', { params });
    return response.data;
  },
  
  // Get all crypto wallets with filtering and pagination
  getWallets: async (params = {}) => {
    const response = await api.get('/admin/crypto/wallets', { params });
    return response.data;
  },
  
  // Get detailed crypto transaction information
  getTransaction: async (id) => {
    const response = await api.get(`/admin/crypto/transactions/${id}`);
    return response.data;
  },
  
  // Get detailed crypto wallet information
  getWallet: async (id) => {
    const response = await api.get(`/admin/crypto/wallets/${id}`);
    return response.data;
  },
  
  // Update crypto transaction details
  updateTransaction: async (id, data) => {
    const response = await api.put(`/admin/crypto/transactions/${id}`, data);
    return response.data;
  },
  
  // Update crypto wallet status
  updateWallet: async (id, data) => {
    const response = await api.put(`/admin/crypto/wallets/${id}`, data);
    return response.data;
  },
  
  // Delete a crypto transaction
  deleteTransaction: async (id) => {
    const response = await api.delete(`/admin/crypto/transactions/${id}`);
    return response.data;
  },
  
  // Get crypto transaction statistics
  getStatistics: async () => {
    const response = await api.get('/admin/crypto/statistics');
    return response.data;
  },
  
  // Get available crypto transaction statuses
  getStatuses: async () => {
    const response = await api.get('/admin/crypto/statuses');
    return response.data;
  },
  
  // Get available crypto transaction types
  getTypes: async () => {
    const response = await api.get('/admin/crypto/types');
    return response.data;
  },
  
  // Get available cryptocurrencies
  getCurrencies: async () => {
    const response = await api.get('/admin/crypto/currencies');
    return response.data;
  },
  
  // Get crypto wallet statuses
  getWalletStatuses: async () => {
    const response = await api.get('/admin/crypto/wallet-statuses');
    return response.data;
  },
  
  // Get all users for dropdown
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/crypto/users', { params });
    return response.data;
  },
  
  // Get all crypto wallets for dropdown
  getWalletsDropdown: async (params = {}) => {
    const response = await api.get('/admin/crypto/wallets-dropdown', { params });
    return response.data;
  },
  
  // Create a new crypto transaction
  createTransaction: async (data) => {
    const response = await api.post('/admin/crypto/transactions', data);
    return response.data;
  },

  // Create a new crypto wallet
  createWallet: async (data) => {
    const response = await api.post('/admin/crypto/wallets', data);
    return response.data;
  },

  // Delete a crypto wallet
  deleteWallet: async (id) => {
    const response = await api.delete(`/admin/crypto/wallets/${id}`);
    return response.data;
  },
};

// Transfer API
export const transferAPI = {
  // Get all transfers with filtering and pagination
  getTransfers: async (params = {}) => {
    const response = await api.get('/admin/transfers', { params });
    return response.data;
  },
  
  // Get detailed transfer information
  getTransfer: async (id) => {
    const response = await api.get(`/admin/transfers/${id}`);
    return response.data;
  },
  
  // Create a new transfer
  createTransfer: async (data) => {
    const response = await api.post('/admin/transfers', data);
    return response.data;
  },
  
  // Update transfer details
  updateTransfer: async (id, data) => {
    const response = await api.put(`/admin/transfers/${id}`, data);
    return response.data;
  },
  
  // Delete a transfer
  deleteTransfer: async (id) => {
    const response = await api.delete(`/admin/transfers/${id}`);
    return response.data;
  },
  
  // Cancel a transfer
  cancelTransfer: async (id) => {
    const response = await api.post(`/admin/transfers/${id}/cancel`);
    return response.data;
  },
  
  // Process a pending transfer
  processTransfer: async (id) => {
    const response = await api.post(`/admin/transfers/${id}/process`);
    return response.data;
  },
  
  // Get transfer statistics
  getStatistics: async () => {
    const response = await api.get('/admin/transfers/statistics');
    return response.data;
  },
  
  // Get available transfer statuses
  getStatuses: async () => {
    const response = await api.get('/admin/transfers/statuses');
    return response.data;
  },
  
  // Get available transfer types
  getTypes: async () => {
    const response = await api.get('/admin/transfers/types');
    return response.data;
  },
  
  // Get all users for dropdown
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/transfers/users', { params });
    return response.data;
  },
  
  // Get all bank accounts for dropdown
  getAccounts: async (params = {}) => {
    const response = await api.get('/admin/transfers/accounts', { params });
    return response.data;
  },
};

// Support API
export const supportAPI = {
  // Get all support tickets with filtering and pagination
  getTickets: async (params = {}) => {
    const response = await api.get('/admin/support', { params });
    return response.data;
  },
  
  // Get detailed support ticket information
  getTicket: async (id) => {
    const response = await api.get(`/admin/support/${id}`);
    return response.data;
  },
  
  // Create a new support ticket
  createTicket: async (data) => {
    const response = await api.post('/admin/support', data);
    return response.data;
  },
  
  // Update support ticket details
  updateTicket: async (id, data) => {
    const response = await api.put(`/admin/support/${id}`, data);
    return response.data;
  },
  
  // Delete a support ticket
  deleteTicket: async (id) => {
    const response = await api.delete(`/admin/support/${id}`);
    return response.data;
  },
  
  // Add response/comment to ticket
  addReply: async (id, data) => {
    const response = await api.post(`/admin/support/${id}/reply`, data);
    return response.data;
  },
  
  // Assign ticket to an agent
  assignTicket: async (id, data) => {
    const response = await api.post(`/admin/support/${id}/assign`, data);
    return response.data;
  },
  
  // Get support ticket statistics
  getStatistics: async () => {
    const response = await api.get('/admin/support/statistics');
    return response.data;
  },
  
  // Get available support ticket statuses
  getStatuses: async () => {
    const response = await api.get('/admin/support/statuses');
    return response.data;
  },
  
  // Get available support ticket categories
  getCategories: async () => {
    const response = await api.get('/admin/support/categories');
    return response.data;
  },
  
  // Get available priority levels
  getPriorities: async () => {
    const response = await api.get('/admin/support/priorities');
    return response.data;
  },
  
  // Get all users for dropdown
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/support/users', { params });
    return response.data;
  },
  
  // Get all staff users for assignment (agents)
  getAgents: async (params = {}) => {
    const response = await api.get('/admin/support/staff-users', { params });
    return response.data;
  },
};

// Cards API (Admin)
export const cardsAPI = {
  // Get all cards with filtering and pagination
  getCards: async (params = {}) => {
    const response = await api.get('/admin/cards', { params });
    return response.data;
  },
  
  // Get detailed card information
  getCard: async (id) => {
    const response = await api.get(`/admin/cards/${id}`);
    return response.data;
  },
  
  // Create a new card
  createCard: async (data) => {
    const response = await api.post('/admin/cards', data);
    return response.data;
  },
  
  // Update card details
  updateCard: async (id, data) => {
    const response = await api.put(`/admin/cards/${id}`, data);
    return response.data;
  },
  
  // Delete a card
  deleteCard: async (id) => {
    const response = await api.delete(`/admin/cards/${id}`);
    return response.data;
  },
  
  // Toggle card status (active/suspended)
  toggleCardStatus: async (id) => {
    const response = await api.post(`/admin/cards/${id}/toggle-status`);
    return response.data;
  },
  
  // Lock a card
  lockCard: async (id) => {
    const response = await api.post(`/admin/cards/${id}/lock`);
    return response.data;
  },
  
  // Unlock a card
  unlockCard: async (id) => {
    const response = await api.post(`/admin/cards/${id}/unlock`);
    return response.data;
  },
  
  // Get card statistics
  getStatistics: async () => {
    const response = await api.get('/admin/cards/statistics');
    return response.data;
  },
  
  // Get available card types
  getCardTypes: async () => {
    const response = await api.get('/admin/cards/card-types');
    return response.data;
  },
  
  // Get available card networks
  getCardNetworks: async () => {
    const response = await api.get('/admin/cards/card-networks');
    return response.data;
  },
  
  // Get available card statuses
  getCardStatuses: async () => {
    const response = await api.get('/admin/cards/card-statuses');
    return response.data;
  },
  
  // Get all users for dropdown
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/cards/users', { params });
    return response.data;
  },
  
  // Get all bank accounts for dropdown
  getBankAccounts: async (params = {}) => {
    const response = await api.get('/admin/cards/bank-accounts', { params });
    return response.data;
  },

  // Bulk update cards
  bulkUpdateCards: async (data) => {
    const response = await api.post('/admin/cards/bulk-update', data);
    return response.data;
  },

  // Regenerate card credentials
  regenerateCard: async (id) => {
    const response = await api.post(`/admin/cards/${id}/regenerate`);
    return response.data;
  },

  // Get card transactions
  getCardTransactions: async (id, params = {}) => {
    const response = await api.get(`/admin/cards/${id}/transactions`, { params });
    return response.data;
  },
};

// Check Deposits API (Admin)
export const adminCheckDepositsAPI = {
  // List check deposits with filtering and pagination
  getDeposits: async (params = {}) => {
    const response = await api.get('/admin/check-deposits', { params });
    return response.data;
  },

  // Get statistics for dashboard widgets
  getStatistics: async () => {
    const response = await api.get('/admin/check-deposits/statistics');
    return response.data;
  },

  // Get single check deposit details
  getDeposit: async (id) => {
    const response = await api.get(`/admin/check-deposits/${id}`);
    return response.data;
  },

  // Approve a pending check deposit
  approveDeposit: async (id) => {
    const response = await api.post(`/admin/check-deposits/${id}/approve`);
    return response.data;
  },

  // Reject a pending check deposit with reason
  rejectDeposit: async (id, reason) => {
    const response = await api.post(`/admin/check-deposits/${id}/reject`, { reason });
    return response.data;
  },

  // Update deposit details (amount, status, check number, images, etc.)
  updateDeposit: async (id, data) => {
    const response = await api.put(`/admin/check-deposits/${id}`, data);
    return response.data;
  },
};

// Admin email API
export const adminEmailAPI = {
  sendEmail: async (data) => {
    const response = await api.post('/admin/emails/send', data);
    return response.data;
  },
};

// Account Transactions API (Admin)
export const accountTransactionsAPI = {
  // Get account transactions
  getTransactions: async (accountId, params = {}) => {
    const response = await api.get(`/admin/accounts/${accountId}/transactions`, { params });
    return response.data;
  },

  // Create transaction
  createTransaction: async (accountId, data) => {
    const response = await api.post(`/admin/accounts/${accountId}/transactions`, data);
    return response.data;
  },

  // Update transaction
  updateTransaction: async (accountId, transactionId, data) => {
    const response = await api.put(`/admin/accounts/${accountId}/transactions/${transactionId}`, data);
    return response.data;
  },

  // Delete transaction
  deleteTransaction: async (accountId, transactionId) => {
    const response = await api.delete(`/admin/accounts/${accountId}/transactions/${transactionId}`);
    return response.data;
  },

  // Get transaction statistics
  getStatistics: async (accountId) => {
    const response = await api.get(`/admin/accounts/${accountId}/transactions/statistics`);
    return response.data;
  },
};

// Countries API
export const countriesAPI = {
  // Get all countries
  getCountries: async () => {
    const response = await api.get('/countries');
    return response.data;
  },

  // Get country by ID
  getCountry: async (id) => {
    const response = await api.get(`/countries/${id}`);
    return response.data;
  },

  // Get countries by currency code
  getCountriesByCurrency: async (currencyCode) => {
    const response = await api.get(`/countries/currency/${currencyCode}`);
    return response.data;
  },

  // Search countries
  searchCountries: async (query) => {
    const response = await api.get(`/countries/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },
};

export default api; 